import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Collapse, Table, Alert, Spinner, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ChevronDown, ChevronUp, TrendingDown, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import sv from 'date-fns/locale/sv';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { ASSET_FORM_STYLES, formatNumber, round2, unwrapData } from './assetUtils';

/**
 * WritedownModal — nedskrivning/återföring (ÅRL 4:5) with the same
 * preview-first shape as DisposalModal: nothing posts until the user has
 * seen exactly what will be booked.
 *   type + amount + date → POST /assets/:id/writedown/preview →
 *   {asset.book_value, netWritedown, warnings, rows} → Bekräfta →
 *   POST /assets/:id/writedown.
 * Backend guards: writedown < bokfört värde (total value loss → utrangering),
 * reversal ≤ prior net writedowns, K2 "uppenbart"-warning for ≤5-year assets.
 */
const WritedownModal = ({ show, onHide, companyId, asset, onDone }) => {
  const [type, setType] = useState('writedown');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date());
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showRows, setShowRows] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show) {
      setType('writedown');
      setAmount('');
      setDate(new Date());
      setPreview(null);
      setShowRows(false);
      setError(null);
    }
  }, [show]);

  const invalidate = () => {
    setPreview(null);
    setError(null);
  };

  const amountNumber = round2(amount);
  const canPreview = amount !== '' && amountNumber > 0;
  const body = () => ({
    type,
    amount: amountNumber,
    date: date.toISOString().split('T')[0],
  });

  const fetchPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/assets/${asset.asset_id}/writedown/preview`),
        body(),
        { withCredentials: true }
      );
      setPreview(unwrapData(response));
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte förhandsgranska nedskrivningen. Försök igen.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/${asset.asset_id}/writedown`),
        body(),
        { withCredentials: true }
      );
      toast.success(type === 'writedown' ? 'Nedskrivningen är bokförd.' : 'Återföringen är bokförd.');
      if (onDone) onDone();
      onHide();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte bokföra nedskrivningen.');
    } finally {
      setConfirming(false);
    }
  };

  const rows = (preview?.rows || []).map((row) => ({
    account_number: row.account,
    label: row.label,
    debit: row.type === 'debit' ? row.amount : 0,
    credit: row.type === 'credit' ? row.amount : 0,
  }));
  const previewBookValue = preview?.asset?.book_value ?? 0;
  const netWritedown = parseFloat(preview?.netWritedown) || 0;

  return (
    <Modal show={show} onHide={confirming ? undefined : onHide} centered contentClassName="asset-form">
      <Modal.Header closeButton={!confirming}>
        <Modal.Title as="h5">Skriv ned {asset?.name || 'tillgång'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Vad vill du göra?</Form.Label>
          <div className="d-flex gap-2">
            <Button
              variant={type === 'writedown' ? 'primary' : 'outline-secondary'}
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => { setType('writedown'); invalidate(); }}
            >
              <TrendingDown size={14} /> Skriv ned värdet
            </Button>
            <Button
              variant={type === 'reversal' ? 'primary' : 'outline-secondary'}
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => { setType('reversal'); invalidate(); }}
            >
              <RotateCcw size={14} /> Återför tidigare nedskrivning
            </Button>
          </div>
          <Form.Text muted>
            {type === 'writedown'
              ? 'Använd när värdet har gått ned varaktigt — t.ex. skada eller att den blivit omodern. Vid totalt värdefall: skrota i stället.'
              : 'Använd när skälet för en tidigare nedskrivning inte längre finns kvar.'}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3" controlId="writedown-amount">
          <Form.Label>Belopp</Form.Label>
          <InputGroup>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); invalidate(); }}
              placeholder="0"
            />
            <InputGroup.Text>kr</InputGroup.Text>
          </InputGroup>
        </Form.Group>

        <Form.Group className="mb-3" controlId="writedown-date">
          <Form.Label>När?</Form.Label>
          <div>
            <DatePicker
              selected={date}
              onChange={(value) => { setDate(value); invalidate(); }}
              dateFormat="yyyy-MM-dd"
              locale={sv}
              className="form-control"
            />
          </div>
        </Form.Group>

        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {!preview && (
          <Button variant="outline-primary" disabled={!canPreview || loadingPreview} onClick={fetchPreview}>
            {loadingPreview ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Räknar…
              </>
            ) : (
              'Visa vad som bokförs'
            )}
          </Button>
        )}

        {preview && (
          <>
            {(preview.warnings || []).map((warning, index) => (
              <Alert key={index} variant="warning" className="py-2" style={{ fontSize: '0.8125rem' }}>
                {warning}
              </Alert>
            ))}
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '1rem 1.25rem',
                fontSize: '0.875rem',
                lineHeight: 1.6,
              }}
            >
              <div>
                Bokfört värde idag: <strong>{formatNumber(previewBookValue)} kr</strong>
              </div>
              <div className="mt-1">
                {type === 'writedown' ? (
                  <span>
                    Efter nedskrivningen blir bokfört värde{' '}
                    <strong>{formatNumber(previewBookValue - amountNumber)} kr</strong> och
                    kommande avskrivningar räknas om på det lägre värdet.
                  </span>
                ) : (
                  <span>
                    Tidigare nedskrivningar netto: <strong>{formatNumber(netWritedown)} kr</strong>.
                    Efter återföringen blir bokfört värde{' '}
                    <strong>{formatNumber(previewBookValue + amountNumber)} kr</strong>.
                  </span>
                )}
              </div>

              {rows.length > 0 && (
                <div className="mt-2">
                  <Button
                    variant="link"
                    size="sm"
                    style={{ textDecoration: 'none', color: 'var(--primary)', paddingLeft: 0 }}
                    onClick={() => setShowRows((prev) => !prev)}
                  >
                    {showRows ? 'Dölj verifikatrader' : 'Visa verifikatrader'}{' '}
                    {showRows ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </Button>
                  <Collapse in={showRows}>
                    <div>
                      <Table size="sm" className="mb-0 mt-1 asset-table">
                        <thead>
                          <tr>
                            <th>Konto</th>
                            <th className="text-end">Debet</th>
                            <th className="text-end">Kredit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, index) => (
                            <tr key={index}>
                              <td>
                                {row.account_number}
                                {row.label ? ` – ${row.label}` : ''}
                              </td>
                              <td className="text-end asset-num">
                                {parseFloat(row.debit) > 0 ? `${formatNumber(row.debit)} kr` : ''}
                              </td>
                              <td className="text-end asset-num">
                                {parseFloat(row.credit) > 0 ? `${formatNumber(row.credit)} kr` : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Collapse>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={confirming}>
          Avbryt
        </Button>
        <Button variant="primary" onClick={confirm} disabled={!preview || confirming}>
          {confirming ? 'Bokför…' : 'Bekräfta'}
        </Button>
      </Modal.Footer>
      <style>{ASSET_FORM_STYLES}</style>
    </Modal>
  );
};

export default WritedownModal;
